import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, combineLatest, from, of, Subject, merge } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, first, map, pluck, shareReplay, switchMap } from 'rxjs/operators';

import { ISubtitle } from 'vbrick-player-src/ISubtitle';
import { ITimeMarker } from 'vbrick-player-src/videogular/controls/VbTimeMarkers.Component';
import { SubtitleService } from 'vbrick-player-src/Subtitle.Service';

import { BootstrapContext } from 'rev-shared/bootstrap/BootstrapContext';
import { ITranscriptionFile } from 'rev-shared/media/ITranscriptionFile';
import { IVideo } from 'rev-shared/media/IVideo';
import { SecondMs } from 'rev-shared/date/Time.Constant';
import { UploadService } from 'rev-shared/media/Upload.Service';
import { isString } from 'rev-shared/util';
import { formatSrt } from 'rev-shared/util/SrtUtil';
import { lastValueFrom } from 'rev-shared/rxjs/lastValueFrom';
import { orderBy } from 'rev-shared/util/SortUtil';

import { IUserTag } from '../inThisVideo/IUserTag';
import { VideoUserTagsService } from '../inThisVideo/VideoUserTags.Service';

import { IPulseTimelineEntry } from './IPulseTimelineEntry';
import { PulseTimelineEntryType } from './PulseTimelineEntryType';
import { VideoPlaybackService } from '../VideoPlayback.Service';

@Injectable({
	providedIn: 'root'
})
export class VideoPulseService {
	public isPulseFilterApplied$: Observable<boolean>;
	public isTranscriptAvailable$: Observable<boolean>;
	public taggedUserRangeMarkers$: Observable<ITimeMarker[]>;
	public timelineEntries$: Observable<IPulseTimelineEntry[]>;
	public transcriptionMarkers$: Observable<ITimeMarker[]>;
	public transcriptionFile$: Observable<ITranscriptionFile>;
	private videoTranscript$: Observable<IPulseTimelineEntry[]>;

	private pulseFilterSubject$ = new BehaviorSubject<string>('');
	private activeSubtitleLanguageSubject$ = new BehaviorSubject<string>('');
	private videoTranscriptSubject$ = new Subject<IPulseTimelineEntry[]>();


	constructor(
		private UploadService: UploadService,
		private SubtitleService: SubtitleService,
		private VideoPlaybackSvc: VideoPlaybackService,
		VideoUserTags: VideoUserTagsService
	) {

		const userTagEntries$: Observable<IPulseTimelineEntry[]> = VideoUserTags.usersTag$.pipe(
			map(userTags => userTags
				.map(userTag => this.userTagToIPulseTimelineEntries(userTag))
				.flat()
			),
			shareReplay({ bufferSize: 1, refCount: true }),
		);

		this.transcriptionFile$ = combineLatest([
			this.activeSubtitleLanguageSubject$,
			VideoPlaybackSvc.video$
		]).pipe(
			map(([language, video]) => {
				if(!video) {
					return;
				}

				return language ?
					video.transcriptionFiles.find(file => file.languageId === language) :
					this.findDefaultLanguage(video.transcriptionFiles);
			})
		);

		this.videoTranscript$ = merge(
			this.transcriptionFile$.pipe(
				switchMap(transcriptionFile => this.getTranscription(transcriptionFile))
			),
			this.videoTranscriptSubject$
		).pipe(
			shareReplay({ bufferSize: 1, refCount: true }),
		);

		const debouncedPulseFilter$: Observable<string> = this.pulseFilterSubject$.pipe(
			debounceTime(500),
			distinctUntilChanged()
		);

		const filteredTimelineEntries$ = combineLatest([
			this.videoTranscript$,
			userTagEntries$,
			debouncedPulseFilter$
		]).pipe(
			map(([transcriptionEntries, userTagEntries, pulseFilter]) => {
				const isPulseFilterApplied: boolean = isString(pulseFilter) && pulseFilter.trim().length > 0;
				const filteredTranscriptionEntries: IPulseTimelineEntry[] = this.caseInsensitivePulseEntryFilter(transcriptionEntries, pulseFilter);
				const filteredUserTagEntries: IPulseTimelineEntry[] = isPulseFilterApplied ?
					this.caseInsensitivePulseEntryFilter(userTagEntries, pulseFilter) :
					[];

				const combinedEntries: IPulseTimelineEntry[] = orderBy(
					[
						...(filteredTranscriptionEntries || []),
						...(filteredUserTagEntries || [])
					],
					entry => entry.startSecs,
					true);

				return {
					combinedEntries,
					isPulseFilterApplied,
					pulseFilter,
					transcriptionEntries: filteredTranscriptionEntries,
					userTagEntries: filteredUserTagEntries
				};
			}),
			//tap(() => window.setTimeout()), // despite using the async filter, doesn't update reliably
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.isPulseFilterApplied$ = filteredTimelineEntries$.pipe(
			pluck('isPulseFilterApplied')
		);

		this.isTranscriptAvailable$ = this.videoTranscript$.pipe(
			map(videoTranscript => videoTranscript && videoTranscript.length > 0)
		);

		this.timelineEntries$ = filteredTimelineEntries$.pipe(
			pluck('combinedEntries'),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.taggedUserRangeMarkers$ = filteredTimelineEntries$.pipe(
			pluck('userTagEntries'),
			map(userTagEntries => this.getTaggedUserRangeMarkers(userTagEntries))
		);

		this.transcriptionMarkers$ = filteredTimelineEntries$.pipe(
			map(filteredTimelineEntries => this.getTranscriptionMarkers(filteredTimelineEntries.transcriptionEntries, filteredTimelineEntries.isPulseFilterApplied)),
		);
	}

	public setFilter(filterValue: string): void {
		if (!isString(filterValue)) {
			return;
		}

		this.pulseFilterSubject$.next(filterValue);
	}

	public setActiveSubtitleLanguage(lang: string): void {
		this.activeSubtitleLanguageSubject$.next(lang);
	}

	private caseInsensitivePulseEntryFilter(entry: IPulseTimelineEntry[], pulseFilter: string): IPulseTimelineEntry[] {
		const lowercasePulseFilter = pulseFilter.toLowerCase();

		return entry ?
			entry.filter(entry => entry.filterValue.toLowerCase().includes(lowercasePulseFilter))
			: null;
	}

	private findDefaultLanguage(files: ITranscriptionFile[]): ITranscriptionFile {
		const { language } = BootstrapContext;
		const exactMatch = files.find(file => file.languageId === language);
		if(exactMatch){
			return exactMatch;
		}

		const stripped = stripCountryCode(language);
		const hasCountryCode = language !== stripped;
		files = orderBy(files, file => file.languageId);

		return hasCountryCode && files.find(file => file.languageId === stripped) ||
			files.find(file => stripCountryCode(file.languageId) === stripped);
	}

	private getTaggedUserRangeMarkers(timelineEntries: IPulseTimelineEntry[]): ITimeMarker[] {
		return timelineEntries
			.filter(entry => entry.startSecs || entry.endSecs) // filter out the entries without a range
			.map(entry => ({
				cssClassName: 'time-range-display',
				endTime: entry.endSecs,
				startTime: entry.startSecs
			}));
	}

	private getTranscription(transcriptionFile: ITranscriptionFile): Observable<IPulseTimelineEntry[]> {
		if (!transcriptionFile) {
			return of(null);
		}

		return from(
			this.SubtitleService.getParsedSubtitle(transcriptionFile.downloadUrl)
		).pipe(
			catchError(() => []),
			map(subtitles => subtitles.map(subtitle => this.subtitleToIPulseTimelineEntry(subtitle)))
		);
	}

	private getTranscriptionMarkers(transcript: IPulseTimelineEntry[], isPulseFilterApplied: boolean): ITimeMarker[] {
		return isPulseFilterApplied ?
			(transcript || []).map(subtitle => this.subtitleToMarker(subtitle)) :
			[];
	}

	private subtitleToIPulseTimelineEntry(subtitle: ISubtitle): IPulseTimelineEntry {
		return {
			endSecs: subtitle.end,
			filterValue: subtitle.text,
			startSecs: subtitle.start,
			text: subtitle.text,
			type: PulseTimelineEntryType.TRANSCRIPTION
		};
	}

	private subtitleToMarker(subtitle: IPulseTimelineEntry): ITimeMarker {
		return {
			startTime: subtitle.startSecs,
			endTime: subtitle.startSecs,
			cssClassName: 'triangle-on-bottom'
		};
	}

	private userTagToIPulseTimelineEntries(userTag: IUserTag): IPulseTimelineEntry[] {
		if (userTag.timeRanges && userTag.timeRanges.length) {
			return userTag.timeRanges.map(timeRange => ({
				endSecs: timeRange.endTimeMs / SecondMs,
				filterValue: userTag.fullName,
				photoUrl: userTag.profileImageUri,
				startSecs: timeRange.startTimeMs / SecondMs,
				text: '',
				type: PulseTimelineEntryType.TAGGED_USER
			}));
		}

		return [{
			endSecs: 0,
			filterValue: userTag.fullName,
			photoUrl: userTag.profileImageUri,
			startSecs: 0,
			text: '',
			type: PulseTimelineEntryType.TAGGED_USER
		}];
	}

	public editSubtitle(video: IVideo, entry: IPulseTimelineEntry, oldEntry: IPulseTimelineEntry): Promise<any> {
		return lastValueFrom(
			combineLatest([
				this.videoTranscript$,
				this.transcriptionFile$
			]).pipe(first())
		)
			.then(([entries, transcript]) => {
				let newEntries: IPulseTimelineEntry[];

				if (entry === undefined) {
					const i = entries.findIndex(e => e === oldEntry);
					if (i >= 0) {
						entries.splice(i, 1);
					}
					newEntries = entries;
				}
				else {
					newEntries = orderBy(
						entries.map(e => e === oldEntry ? entry : e),
						entry => entry.startSecs,
						true);
				}

				const srt = formatSrt(
					newEntries
						.filter(e => e.type === PulseTimelineEntryType.TRANSCRIPTION)
						.map(toSubtitle));
				const language = transcript.languageId;

				return this.UploadService.replaceTranscription(video.id, language, srt)
					.then(() => {
						const url = URL.createObjectURL(new Blob([srt]));

						const subtitles = video.subtitles = video.subtitles.map(s => s.languageCode !== language ? s : ({
							src: url,
							languageCode: language
						}));

						const transcriptionFiles = video.transcriptionFiles = video.transcriptionFiles.map(t => t.languageId !== language ? t : {
							...t,
							downloadUrl: url
						});

						this.videoTranscriptSubject$.next(newEntries);
						this.VideoPlaybackSvc.mergeVideoPartial({ subtitles, transcriptionFiles });
					});
			})
			.catch(err =>
				console.error('Error saving subtitles: ', err));
	}
}

function stripCountryCode(language: string): string {
	return language.split('-')[0];
}

function toSubtitle(entry: IPulseTimelineEntry): ISubtitle {
	return {
		text: entry.text,
		start: entry.startSecs,
		end: entry.endSecs
	};
}
